import {
  HHBox,
  HHButton,
  HHChip,
  HHCircularProgress,
  HHGrid,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  CloudOff,
  FilterList,
  Search,
  ViewList,
  ViewModule,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocalStorage } from '../../../../../utils/local-storage';
import { tabRoutes } from '../../constants/hinge-connect-constants.constant';
import { rulesRoutes } from '../constants/rules.constant';
import { LOCAL_STORAGE_KEYS } from '../contexts/list-rules.context';
import { useRulesPermissions } from '../hooks/use-rules-permissions';
import { RuleSource, useSources } from '../hooks/use-sources';
import { CreateRuleFab } from './create-rule-fab';
import { SourceCard } from './source-card';

// Add new localStorage key for view preference
const VIEW_PREFERENCE_KEY = 'hingeConnect.rules.dashboard.viewPreference';

export const RulesDashboard = (): JSX.Element => {
  const navigate = useNavigate();
  const { sources, loading, error } = useSources();
  const permissions = useRulesPermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'rows'>('cards');

  // Load view preference from localStorage on component mount
  useEffect(() => {
    const savedView = LocalStorage.get(VIEW_PREFERENCE_KEY);
    if (savedView === 'rows' || savedView === 'cards') {
      setViewMode(savedView);
    }
  }, []);

  // Save view preference to localStorage when it changes
  const handleViewToggle = (): void => {
    const newView = viewMode === 'cards' ? 'rows' : 'cards';
    setViewMode(newView);
    LocalStorage.set(VIEW_PREFERENCE_KEY, newView);
  };

  // Filter sources based on search query
  const filteredSources = useMemo(() => {
    if (!searchQuery.trim()) return sources;

    const query = searchQuery.toLowerCase().trim();
    return sources.filter(source =>
      source.source.toLowerCase().includes(query),
    );
  }, [sources, searchQuery]);

  const activeSources = filteredSources.filter(s => s.status === 'active');
  const inactiveSources = filteredSources.filter(s => s.status === 'inactive');

  const handleSourceClick = (source: string): void => {
    // Persist selected source and clear other filters for list view
    LocalStorage.set(LOCAL_STORAGE_KEYS.selectedSource, source);
    LocalStorage.set(LOCAL_STORAGE_KEYS.selectedStatus, '');
    LocalStorage.set(LOCAL_STORAGE_KEYS.selectedResourceTypes, []);
    navigate(`/${tabRoutes.baseRoute}/${tabRoutes.rules}/${rulesRoutes.list}`);
  };

  // Row view component
  const SourceRow = ({ source }: { source: RuleSource }): JSX.Element => {
    const isActive = source.status === 'active';
    const colorSet = isActive
      ? {
          primary: '#2E7D32',
          background: '#F1F8E9',
          border: '#4CAF50',
        }
      : {
          primary: '#757575',
          background: '#FAFAFA',
          border: '#BDBDBD',
        };

    const formatDate = (dateString: string): string => {
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      } catch {
        return 'Unknown';
      }
    };

    return (
      <HHBox
        onClick={(): void => handleSourceClick(source.source)}
        sx={{
          cursor: 'pointer',
          padding: '16px 24px',
          border: `1px solid ${colorSet.border}`,
          borderRadius: '12px',
          background: colorSet.background,
          '&:hover': {
            boxShadow: `0 4px 20px rgba(0, 0, 0, 0.1), 0 0 0 1px ${colorSet.border}`,
            transform: 'translateY(-2px)',
          },
          '&:focus-visible': {
            outline: `3px solid ${colorSet.primary}`,
            outlineOffset: '2px',
          },
          transition: 'all 0.2s ease',
        }}
        tabIndex={0}
        role="button"
        onKeyDown={(e): void => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSourceClick(source.source);
          }
        }}
      >
        <HHStack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={3}
        >
          <HHTypography
            hhVariant="h6"
            sx={{
              fontWeight: 600,
              minWidth: '120px',
              color: colorSet.primary,
            }}
          >
            {source.source}
          </HHTypography>

          <HHStack
            direction="row"
            alignItems="center"
            spacing={3}
            sx={{ marginLeft: 'auto' }}
          >
            <HHTypography
              hhVariant="body2"
              sx={{
                color: 'text.secondary',
                minWidth: '100px',
                textAlign: 'right',
              }}
            >
              {formatDate(source.lastUpdated)}
            </HHTypography>

            <HHTypography hhVariant="body2" sx={{ color: 'text.secondary' }}>
              {source.rulesCount} rule{source.rulesCount !== 1 ? 's' : ''}
            </HHTypography>

            <HHChip
              label={isActive ? 'Active' : 'Inactive'}
              color={isActive ? 'success' : 'default'}
              size="small"
              hhVariant="filled"
            />
          </HHStack>
        </HHStack>
      </HHBox>
    );
  };

  if (loading) {
    return (
      <HHBox paddingX="0" bgcolor="#ffffff">
        <HHStack direction="column" spacing={6} height="100%">
          <HHBox
            sx={{ display: 'flex', justifyContent: 'center', paddingY: 6 }}
          >
            <HHCircularProgress />
          </HHBox>
        </HHStack>
      </HHBox>
    );
  }

  if (error) {
    return (
      <HHBox paddingX="0" bgcolor="#ffffff">
        <HHStack
          direction="column"
          spacing={4}
          alignItems="center"
          paddingY={6}
        >
          <CloudOff color="error" sx={{ fontSize: 48 }} />
          <HHTypography hhVariant="h5">Unable to load sources</HHTypography>
        </HHStack>
      </HHBox>
    );
  }

  return (
    <HHBox paddingX="0" bgcolor="#ffffff">
      <HHStack direction="column" spacing={6}>
        <HHBox sx={{ paddingX: '2rem', paddingTop: '2rem' }}>
          <HHStack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <HHStack direction="row" alignItems="center" spacing={2}>
              <FilterList sx={{ color: 'text.primary' }} />
              <HHTypography hhVariant="h1">Rules by Source</HHTypography>
            </HHStack>

            <HHStack direction="row" alignItems="center" spacing={2}>
              <HHTextField
                hhVariant="outlined"
                placeholder="Search Sources..."
                value={searchQuery}
                onChange={(e): void => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ color: 'text.secondary', mr: 1 }} />
                  ),
                }}
                sx={{
                  minWidth: '280px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />

              <HHButton
                hhVariant="outlined"
                onClick={handleViewToggle}
                aria-label="Toggle view"
                sx={{
                  minWidth: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                  },
                }}
              >
                {viewMode === 'cards' ? <ViewList /> : <ViewModule />}
              </HHButton>
            </HHStack>
          </HHStack>
          <HHTypography
            hhVariant="body1"
            sx={{ color: 'text.secondary', marginTop: '4px' }}
          >
            Choose a source to explore its rules
          </HHTypography>
        </HHBox>

        {filteredSources.length === 0 ? (
          <HHBox sx={{ paddingX: '2rem' }}>
            <HHTypography hhVariant="body1" sx={{ color: 'text.secondary' }}>
              {searchQuery.trim()
                ? 'No sources found matching your search.'
                : 'No rules found.'}
            </HHTypography>
          </HHBox>
        ) : (
          <>
            {activeSources.length > 0 && (
              <HHBox sx={{ paddingX: '2rem' }}>
                <HHStack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ marginBottom: 3 }}
                >
                  <HHBox
                    sx={{
                      width: '8px',
                      height: '24px',
                      backgroundColor: '#2E7D32',
                      borderRadius: '4px',
                    }}
                  />
                  <HHTypography
                    hhVariant="h5"
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    Active Sources ({activeSources.length})
                  </HHTypography>
                </HHStack>

                {viewMode === 'cards' ? (
                  <HHGrid container spacing={4}>
                    {activeSources.map(source => (
                      <HHGrid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={source.source}
                      >
                        <SourceCard
                          source={source}
                          onClick={handleSourceClick}
                        />
                      </HHGrid>
                    ))}
                  </HHGrid>
                ) : (
                  <HHStack direction="column" spacing={2}>
                    {activeSources.map(source => (
                      <SourceRow key={source.source} source={source} />
                    ))}
                  </HHStack>
                )}
              </HHBox>
            )}

            {inactiveSources.length > 0 && (
              <HHBox sx={{ paddingX: '2rem' }}>
                <HHStack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    marginTop: viewMode === 'rows' ? 0 : 2,
                    marginBottom: 3,
                  }}
                >
                  <HHBox
                    sx={{
                      width: '8px',
                      height: '24px',
                      backgroundColor: '#757575',
                      borderRadius: '4px',
                    }}
                  />
                  <HHTypography
                    hhVariant="h5"
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    Inactive Sources ({inactiveSources.length})
                  </HHTypography>
                </HHStack>

                {viewMode === 'cards' ? (
                  <HHGrid container spacing={4}>
                    {inactiveSources.map(source => (
                      <HHGrid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={source.source}
                      >
                        <SourceCard
                          source={source}
                          onClick={handleSourceClick}
                        />
                      </HHGrid>
                    ))}
                  </HHGrid>
                ) : (
                  <HHStack direction="column" spacing={2}>
                    {inactiveSources.map(source => (
                      <SourceRow key={source.source} source={source} />
                    ))}
                  </HHStack>
                )}
              </HHBox>
            )}
          </>
        )}
      </HHStack>
      {permissions.canCreate && <CreateRuleFab />}
    </HHBox>
  );
};
